/**
 * gpc-privacy-control.js
 * 用途: 检测 Global Privacy Control (GPC) 信号，若用户已启用 GPC（navigator.globalPrivacyControl === true），
 *       在 Shopify Cookie 偏好弹窗打开时，自动取消勾选 Personalization、Marketing、Analytics 三项。
 *
 * 特性:
 *   - 全局配置项 window.__GPC_CONFIG__.enabled 控制是否启用（由 theme.liquid 注入）
 *   - 只对已勾选的项执行取消操作，未勾选的不处理
 *   - 使用 MutationObserver 监听弹窗插入及 data-consent 属性变化，覆盖两种弹出方式
 *   - 每次弹窗开启仅执行一次，避免重复触发
 *
 * Shopify Cookie 弹窗 DOM 说明（实测结构）:
 *   弹窗根节点: <section id="shopify-pc__prefs__dialog"
 *                         class="shopify-pc__prefs__dialog"
 *                         role="dialog"
 *                         data-consent="000">
 *   三个可控选项（ID）:
 *     #shopify-pc__prefs__preferences  → Personalization
 *     #shopify-pc__prefs__marketing    → Marketing
 *     #shopify-pc__prefs__analytics    → Analytics
 *   每个选项内 label > span > svg[data-icon-type]:
 *     data-icon-type="checked"   → style="display: block;"  表示已勾选
 *     data-icon-type="unchecked" → style="display: none;"   表示已勾选
 *   取消勾选：点击该选项对应的 label 即可切换 checkbox 状态
 */

;(function () {
  'use strict';

  // ── 1. 读取全局配置 ──────────────────────────────────────────────────────
  var config = window.__GPC_CONFIG__ || {};
  var enabled = config.enabled !== false; // 默认启用，theme.liquid 注入 enabled: false 时关闭

  if (!enabled) return;

  // ── 2. 检测 GPC 信号 ────────────────────────────────────────────────────
  // navigator.globalPrivacyControl 由支持 GPC 的浏览器或隐私插件注入
  // 规范: https://globalprivacycontrol.org/
  if (navigator.globalPrivacyControl !== true) return;

  // ── 3. 需要取消勾选的 Cookie 分类（三项）────────────────────────────────
  var PREF_IDS = [
    'shopify-pc__prefs__preferences', // Personalization
    'shopify-pc__prefs__marketing',   // Marketing
    'shopify-pc__prefs__analytics',   // Analytics
  ];

  /**
   * 检查某个偏好选项是否已勾选
   * 判断依据：选项内 svg[data-icon-type="checked"] 的 display 不为 "none"
   *
   * @param {HTMLElement} prefOption - #shopify-pc__prefs__xxx 元素
   * @returns {boolean}
   */
  function isChecked(prefOption) {
    var checkedSvg = prefOption.querySelector('svg[data-icon-type="checked"]');
    if (!checkedSvg) return false;
    // Shopify 通过 inline style 切换 SVG 可见性
    return checkedSvg.style.display !== 'none';
  }

  /**
   * 对已勾选的偏好选项执行取消操作
   * 通过点击其 label 来触发 checkbox 状态切换（与用户操作完全一致）
   *
   * @param {HTMLElement} prefOption - #shopify-pc__prefs__xxx 元素
   */
  function uncheckPref(prefOption) {
    if (!isChecked(prefOption)) return; // 未勾选则跳过

    var label = prefOption.querySelector('label');
    if (label) {
      label.click();
    }
  }

  /**
   * 对弹窗中所有目标选项执行取消操作
   * @param {HTMLElement} dialogRoot - Cookie 偏好弹窗的根元素
   */
  function applyGpcUnchecks(dialogRoot) {
    PREF_IDS.forEach(function (id) {
      var prefOption = dialogRoot.querySelector('#' + id);
      if (prefOption) {
        uncheckPref(prefOption);
      }
    });
  }

  // ── 4. 监听弹窗出现（MutationObserver）──────────────────────────────────
  // Shopify Cookie 弹窗根节点 id 固定为 "shopify-pc__prefs__dialog"
  // 两种触发场景：
  //   a) 弹窗节点被动态插入 DOM（childList）
  //   b) 弹窗已在 DOM 中，通过 data-consent 属性变化来显示（attributes）

  var DIALOG_ID = 'shopify-pc__prefs__dialog';

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];

      // 场景 a：新节点插入，检查是否是弹窗本身或包含弹窗
      if (mutation.type === 'childList') {
        for (var j = 0; j < mutation.addedNodes.length; j++) {
          var node = mutation.addedNodes[j];
          if (node.nodeType !== 1) continue;

          var dialog = node.id === DIALOG_ID
            ? node
            : node.querySelector('#' + DIALOG_ID);

          if (dialog) {
            // 稍等一帧确保内部 checkbox 状态已初始化
            requestAnimationFrame(function () {
              applyGpcUnchecks(dialog);
            });
            return;
          }
        }
      }

      // 场景 b：data-consent 属性变化，说明弹窗状态更新（如从隐藏变为显示）
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-consent' &&
        mutation.target.id === DIALOG_ID
      ) {
        var target = mutation.target;
        requestAnimationFrame(function () {
          applyGpcUnchecks(target);
        });
      }
    }
  });

  // 开始监听
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-consent'],
  });

  // ── 5. 处理页面加载时弹窗已在 DOM 中的情况 ─────────────────────────────
  // Shopify 会将弹窗直接渲染到 HTML，脚本执行时它已存在
  document.addEventListener('DOMContentLoaded', function () {
    var dialog = document.getElementById(DIALOG_ID);
    if (dialog) {
      requestAnimationFrame(function () {
        applyGpcUnchecks(dialog);
      });
    }
  });
})();
